"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Client = void 0;
const isomorphic_ws_1 = __importDefault(require("isomorphic-ws"));
class Client {
    constructor(url) {
        this.txs = {};
        this.url = url;
        this.connections = {
            txs: undefined,
        };
    }
    heartbeat(topic) {
        const connection = this.connections[topic];
        if (!connection)
            return;
        connection.pingTimeout && clearTimeout(connection.pingTimeout);
        connection.pingTimeout = setTimeout(() => connection === null || connection === void 0 ? void 0 : connection.ws.close(), 10000 + 5000);
    }
    onOpen(topic, resolve) {
        this.heartbeat(topic);
        resolve(true);
    }
    /**
     * Subscribe to transaction history and updates for newly confirmed and pending transactions.
     *
     * - Subsequent calls to `subscribeTxs` for the same `subscriptionId` will add additional addresses to be watched.
     *
     * @param subscriptionId unique id for grouping of addresses
     * @param data details for subscribe
     * @param data.topic specifies which topic to subscribe to
     * @param data.addresses list of addresses to subscribe to
     * @param onMessage handler for all transaction messages associated with `subscriptionId`
     * @param [onError] optional handler for any error messages associated with `subscriptionId`
     */
    subscribeTxs(subscriptionId, data, onMessage, onError) {
        return __awaiter(this, void 0, void 0, function* () {
            // keep track of the onMessage and onError handlers associated with each subscriptionId
            this.txs[subscriptionId] = { onMessage, onError, data: undefined };
            if (this.connections.txs) {
                if (this.connections.txs.ws.readyState === 1) {
                    // subscribe if connection exists and is ready
                    this.connections.txs.ws.send(JSON.stringify({ subscriptionId, method: 'subscribe', data }));
                }
                else {
                    // queue up unique addresses for subscriptions if connection exists, but is not ready yet
                    const txsData = this.txs[subscriptionId].data;
                    this.txs[subscriptionId].data = txsData
                        ? Object.assign(Object.assign({}, txsData), { addresses: [...new Set(...txsData.addresses, ...data.addresses)] }) : data;
                }
                return;
            }
            const ws = new isomorphic_ws_1.default(this.url);
            this.connections.txs = { ws };
            // send connection errors to all subscription onError handlers
            ws.onerror = (event) => {
                Object.entries(this.txs).forEach(([subscriptionId, { onError }]) => {
                    onError && onError({ subscriptionId, type: 'error', message: event.message });
                });
            };
            // clear heartbeat timeout on close
            ws.onclose = () => { var _a; return ((_a = this.connections.txs) === null || _a === void 0 ? void 0 : _a.pingTimeout) && clearTimeout(this.connections.txs.pingTimeout); };
            ws.onmessage = (event) => {
                var _a, _b;
                if (!event)
                    return;
                // TODO: check event.type and handle non desired messages separately (noop)
                // trigger heartbeat keep alive on ping event
                if (event.data === 'ping') {
                    this.heartbeat('txs');
                    return;
                }
                try {
                    const message = JSON.parse(event.data.toString());
                    // narrow type to ErrorResponse if key `type` exists and forward to correct onError handler
                    if ('type' in message) {
                        const onErrorHandler = (_a = this.txs[message.subscriptionId]) === null || _a === void 0 ? void 0 : _a.onError;
                        onErrorHandler && onErrorHandler(message);
                        return;
                    }
                    // forward the transaction message to the correct onMessage handler
                    const onMessageHandler = (_b = this.txs[message.subscriptionId]) === null || _b === void 0 ? void 0 : _b.onMessage;
                    onMessageHandler && onMessageHandler(message.data);
                }
                catch (err) {
                    console.log(`failed to handle onmessage event: ${JSON.stringify(event)}: ${err}`);
                }
            };
            // wait for the connection to open
            yield new Promise((resolve) => {
                ws.onopen = () => {
                    // start heartbeat
                    this.onOpen('txs', resolve);
                    // subscribe to all queued subscriptions
                    Object.values(this.txs).forEach(({ data }) => {
                        if (!data)
                            return;
                        const payload = { subscriptionId, method: 'subscribe', data };
                        ws.send(JSON.stringify(payload));
                        delete this.txs[subscriptionId].data;
                    });
                    // subscribe to initial subscription
                    const payload = { subscriptionId, method: 'subscribe', data };
                    ws.send(JSON.stringify(payload));
                };
            });
        });
    }
    /**
     * Unsubscribe from transaction history and updates for newly confirmed and pending transactions.
     *
     * - If `subscriptionId` is provided, any provided addresses will be unsubscribed from.
     *   If no addresses are provided, the subscription will be unsubscribed from.
     *
     * - If `subscriptionId` is not provided, all subscriptions will be unsubscribed from.
     *
     * @param [subscriptionId] unique identifier to unsubscribe from
     * @param [data] details for unsubscribe
     * @param data.topic specifies which topic to unsubscribe from
     * @param data.addresses list of addresses to unsubscribe from
     */
    unsubscribeTxs(subscriptionId, data) {
        var _a, _b;
        if (!subscriptionId)
            this.txs = {};
        if (subscriptionId && !(data === null || data === void 0 ? void 0 : data.addresses.length))
            delete this.txs[subscriptionId];
        const payload = {
            subscriptionId: subscriptionId !== null && subscriptionId !== void 0 ? subscriptionId : '',
            method: 'unsubscribe',
            data: { topic: 'txs', addresses: (_a = data === null || data === void 0 ? void 0 : data.addresses) !== null && _a !== void 0 ? _a : [] },
        };
        (_b = this.connections.txs) === null || _b === void 0 ? void 0 : _b.ws.send(JSON.stringify(payload));
    }
    /**
     * Close and unsubscribe from any subscriptions
     *
     * - If no topic is provided, all supported topics will be closed and unsubscribed from
     *
     * @param [topic] specifies which topic to close and unsubscribe from
     */
    close(topic) {
        const closeTxs = () => {
            var _a;
            this.unsubscribeTxs();
            (_a = this.connections.txs) === null || _a === void 0 ? void 0 : _a.ws.close();
        };
        // close all connections if no topic is provided
        if (!topic) {
            closeTxs();
            return;
        }
        switch (topic) {
            case 'txs':
                closeTxs();
                break;
            default:
                console.log(`topic: ${topic} not supported`);
        }
    }
}
exports.Client = Client;
